import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { vocabularyTestActions } from 'pages/VocabularyTest/store/vocabularyTest.slice';
import { fillUrlIds, pagePaths } from 'routes/paths';
import { fmt } from 'pages/VocabularyTest/cefr';

const TESTS_PER_PAGE = 5;

// Minimum desktop row height — keeps rows visually uniform. The reservation
// spacer does NOT derive from this; it uses the measured rendered row height
// (see measuredRowHeight) so borders/padding are accounted for exactly.
const ROW_MIN_HEIGHT = 53;

const POSITIVE = '#3B6D11';

const formatDate = (iso: string) => dayjs(iso).format('DD.MM.YYYY');

const TestHistory: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    data: historyData,
    number: pageNumber,
    size: pageSize,
    totalElements,
    totalPages,
  } = useAppSelector((store) => store.vocabularyTest.vocabularyTestHistory);

  const [testIdToDelete, setTestIdToDelete] = useState<string | null>(null);

  // Measure the actual rendered height of one row (border + padding + content
  // included) so the "reserve the missing rows' space" spacer is exact —
  // derived from the DOM, not a hardcoded guess that drifts by the rows'
  // border widths. Re-measured whenever the rows change or the row resizes
  // (e.g. viewport width change reflowing content). Rows share one identical
  // layout, so measuring the first one is representative of all of them.
  const firstRowRef = useRef<HTMLDivElement | null>(null);
  const [measuredRowHeight, setMeasuredRowHeight] = useState(0);

  useLayoutEffect(() => {
    const el = firstRowRef.current;
    if (!el) {
      setMeasuredRowHeight(0);
      return;
    }
    const measure = () => setMeasuredRowHeight(el.offsetHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [historyData]);

  const maxCount = useMemo(
    () =>
      historyData.reduce(
        (acc, t) => (t.learnedCount > acc ? t.learnedCount : acc),
        0,
      ) || 1,
    [historyData],
  );

  const currentPage = pageNumber + 1;
  const safeTotalPages = Math.max(1, totalPages);
  // Reserve empty rows on a partial last page so every page is the same
  // height and the card doesn't shrink when the user paginates. Only matters
  // when there's more than one page.
  const referencePageSize = pageSize || TESTS_PER_PAGE;
  const fillerRowsCount =
    safeTotalPages > 1
      ? Math.max(0, referencePageSize - historyData.length)
      : 0;

  const changePage = (page: number) => {
    dispatch(
      vocabularyTestActions.vocabularyTestHistoryGet({
        page: page - 1,
        size: pageSize || TESTS_PER_PAGE,
      }),
    );
  };

  const openResult = (id: string) =>
    navigate(
      fillUrlIds(pagePaths.vocabularyTestResult, {
        vocabularyTestResultId: id,
      }),
    );

  const openRow = (test: (typeof historyData)[number]) => {
    if (test.status === 'IN_PROGRESS') {
      navigate('/' + pagePaths.vocabularyTestRun);
      return;
    }
    openResult(test.id);
  };

  const closeDeleteDialog = () => setTestIdToDelete(null);

  const confirmDelete = () => {
    if (testIdToDelete) {
      dispatch(vocabularyTestActions.deleteTestHistory(testIdToDelete));
    }
    closeDeleteDialog();
  };

  if (!historyData.length) {
    return null;
  }

  // Proportional (fr) columns so they share the row width evenly and adapt to
  // the container instead of one greedy 1fr eating all the slack next to fixed
  // px columns. The bar column gets a little extra; actions stays content-sized.
  const GRID_COLS_MD =
    'minmax(96px, 1fr) minmax(120px, 1fr) minmax(200px, 2.2fr) minmax(96px, 1fr) 56px';
  const GRID_COLS_XS = 'minmax(0, 1fr) auto auto';

  return (
    <Box pb={4}>
      <Stack
        alignItems={'baseline'}
        direction={'row'}
        justifyContent={'space-between'}
        sx={{ mb: 1.5 }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
          {'Історія тестів'}
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
          {`Усього ${historyData.length}`}
        </Typography>
      </Stack>

      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'primary.tableBorder',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Header — desktop only */}
        <Box
          sx={{
            // No bottom border here — the first data row's own top border
            // (desktop) provides the header/row separator, keeping all rows a
            // single uniform box model for measurement.
            bgcolor: 'background.default',
            display: { xs: 'none', md: 'grid' },
            gap: 2,
            gridTemplateColumns: GRID_COLS_MD,
            px: 2.5,
            py: 1.25,
          }}
        >
          {['Дата', 'Статус', 'Запас слів', 'Нові слова', ''].map((h, i) => (
            <Typography
              key={i}
              sx={{
                color: 'text.secondary',
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.06em',
                textAlign: 'center',
                textTransform: 'uppercase',
              }}
            >
              {h}
            </Typography>
          ))}
        </Box>

        {historyData.map((test, index) => {
          const isInProgress = test.status === 'IN_PROGRESS';
          // "Дата" column shows when the test started (createdAt). For a
          // finished test whose completion fell on a different day, the end
          // date is shown as a muted second line — same-day tests skip it to
          // avoid a redundant duplicate.
          const startDate = formatDate(test.createdAt);
          const endDate = formatDate(test.updatedAt);
          const showEndDate = !isInProgress && endDate !== startDate;
          const barPct = Math.min(100, (test.learnedCount / maxCount) * 100);
          // "Нові слова": words this test added to the vocabulary that weren't
          // already known. Always >= 0; null/absent for pre-feature tests.
          const newlyAdded = test.newlyAddedCount ?? null;
          const hasNewlyAdded = typeof newlyAdded === 'number' && newlyAdded > 0;

          return (
            <Box
              key={test.id}
              ref={index === 0 ? firstRowRef : undefined}
              onClick={() => openRow(test)}
              sx={{
                alignItems: 'center',
                // Every row carries its own top border on desktop (the first
                // row's replaces the header's bottom border) so all rows share
                // one identical box model — that's what makes measuring one row
                // representative of the missing ones. On mobile there's no
                // header, so the first row keeps no top line.
                borderColor: 'primary.tableBorder',
                borderTop: {
                  xs: index > 0 ? '1px solid' : 'none',
                  md: '1px solid',
                },
                cursor: 'pointer',
                display: 'grid',
                gap: { xs: 1.25, md: 2 },
                gridTemplateColumns: {
                  xs: GRID_COLS_XS,
                  md: GRID_COLS_MD,
                },
                minHeight: { xs: 'auto', md: ROW_MIN_HEIGHT },
                px: { xs: 1.75, md: 2.5 },
                py: 1.5,
                transition: 'background-color 120ms ease',
                '&:hover': { bgcolor: 'primary.body' },
              }}
            >
              <Box
                sx={{
                  order: { xs: 1, md: 0 },
                  textAlign: { xs: 'left', md: 'center' },
                }}
              >
                <Typography sx={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                  {startDate}
                </Typography>
                {showEndDate && (
                  <Typography
                    sx={{
                      color: 'text.secondary',
                      fontSize: 12,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {`→ ${endDate}`}
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'flex-start', md: 'center' },
                  order: { xs: 3, md: 0 },
                  gridColumn: { xs: '1 / -1', md: 'auto' },
                }}
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    // Same box model (bg tint, no border) AND a shared minWidth
                    // (fits the longer "Завершено" label) so the chips render
                    // at an identical size regardless of label length — only
                    // the color tells "Активний" and "Завершено" apart.
                    bgcolor: isInProgress
                      ? 'rgba(59, 109, 17, 0.12)'
                      : 'primary.tableHeadBg',
                    borderRadius: 999,
                    color: isInProgress ? POSITIVE : 'text.secondary',
                    display: 'inline-flex',
                    fontSize: 12,
                    fontWeight: 800,
                    height: 24,
                    justifyContent: 'center',
                    letterSpacing: '0.04em',
                    minWidth: 104,
                    px: 1.25,
                  }}
                >
                  {isInProgress ? 'Активний' : 'Завершено'}
                </Box>
              </Box>

              <Stack
                direction={'row'}
                alignItems={'center'}
                spacing={1.5}
                sx={{
                  justifyContent: { xs: 'flex-start', md: 'center' },
                  minWidth: 0,
                  order: { xs: 4, md: 0 },
                  gridColumn: { xs: '1 / -1', md: 'auto' },
                }}
              >
                <Box
                  sx={{
                    bgcolor: 'primary.purple',
                    borderRadius: 999,
                    display: { xs: 'none', sm: 'block' },
                    // Long-ish bar (this column is intentionally wider than the
                    // others), but still leaves room for the count + margins so
                    // it doesn't run edge-to-edge.
                    flex: 'none',
                    height: 8,
                    maxWidth: 300,
                    minWidth: 80,
                    overflow: 'hidden',
                    width: '80%',
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: 'primary.main',
                      height: '100%',
                      opacity: 0.7,
                      width: `${barPct}%`,
                    }}
                  />
                </Box>
                <Typography
                  sx={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap' }}
                >
                  {fmt(test.learnedCount)}
                </Typography>
                {/* Mobile inline "Нові слова" — only when the test added any */}
                {hasNewlyAdded && (
                  <Stack
                    direction={'row'}
                    alignItems={'center'}
                    spacing={0.25}
                    sx={{
                      color: POSITIVE,
                      display: { xs: 'inline-flex', md: 'none' },
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  >
                    <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                    <Box component={'span'}>{`+${fmt(newlyAdded)}`}</Box>
                  </Stack>
                )}
              </Stack>

              {/* Desktop "Нові слова" column */}
              <Stack
                direction={'row'}
                alignItems={'center'}
                spacing={0.25}
                sx={{
                  color: hasNewlyAdded ? POSITIVE : 'text.secondary',
                  display: { xs: 'none', md: 'inline-flex' },
                  fontSize: 14,
                  fontWeight: 700,
                  justifySelf: 'center',
                }}
              >
                {hasNewlyAdded && <ArrowUpwardIcon sx={{ fontSize: 15 }} />}
                <Box component={'span'}>
                  {hasNewlyAdded ? `+${fmt(newlyAdded)}` : '—'}
                </Box>
              </Stack>

              <Stack
                direction={'row'}
                spacing={0.25}
                sx={{
                  justifySelf: { xs: 'flex-end', md: 'center' },
                  order: { xs: 2, md: 0 },
                }}
              >
                <IconButton
                  aria-label={'Видалити запис історії'}
                  size={'small'}
                  onClick={(event) => {
                    event.stopPropagation();
                    setTestIdToDelete(test.id);
                  }}
                  sx={{ color: 'text.secondary' }}
                >
                  <DeleteOutlineIcon fontSize={'small'} />
                </IconButton>
                <ChevronRightIcon
                  sx={{ alignSelf: 'center', color: 'text.secondary' }}
                />
              </Stack>
            </Box>
          );
        })}
      </Box>

      {/* Reserve the missing rows' height OUTSIDE the card so the space reads
          as page background (not a white void / empty rows inside the card).
          The card shrinks to fit its actual rows; this transparent spacer
          keeps the section's total height constant so the content below never
          jumps when paginating. Height = missing rows × the *measured* row
          height (see measuredRowHeight) — exact to the pixel regardless of
          borders/padding. Desktop only — mobile rows are taller / multi-line,
          so no fixed-height reservation there. */}
      {fillerRowsCount > 0 && measuredRowHeight > 0 && (
        <Box
          aria-hidden
          sx={{
            display: { xs: 'none', md: 'block' },
            height: fillerRowsCount * measuredRowHeight,
          }}
        />
      )}

      {safeTotalPages > 1 && (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            justifyContent: 'center',
            mt: 2,
            position: 'relative',
            py: 1.5,
          }}
        >
          <Typography
            sx={{
              color: 'text.secondary',
              display: { xs: 'none', md: 'block' },
              fontSize: 13,
              left: 0,
              position: 'absolute',
            }}
          >
            {`${pageNumber * pageSize + 1}–${pageNumber * pageSize + historyData.length} з ${totalElements}`}
          </Typography>
          <Stack direction={'row'} alignItems={'center'} spacing={1}>
            <IconButton
              size={'small'}
              disabled={currentPage === 1}
              onClick={() => changePage(currentPage - 1)}
              sx={{
                border: '1px solid',
                borderColor: 'primary.tableBorder',
                borderRadius: 1,
              }}
            >
              <ChevronLeftIcon fontSize={'small'} />
            </IconButton>
            {Array.from({ length: safeTotalPages }, (_, idx) => {
              const page = idx + 1;
              const isActive = page === currentPage;
              return (
                <Button
                  key={page}
                  onClick={() => changePage(page)}
                  size={'small'}
                  variant={'outlined'}
                  sx={{
                    // Active page: solid primary fill + white text (the theme
                    // renders `contained` as a bordered transparent button, so
                    // set the fill explicitly here).
                    backgroundColor: isActive ? 'primary.main' : 'transparent',
                    borderColor: isActive ? 'primary.main' : 'divider',
                    color: isActive ? 'common.white' : 'text.primary',
                    fontWeight: isActive ? 700 : 500,
                    minHeight: 36,
                    minWidth: 36,
                    px: 1.25,
                    '&:hover': {
                      backgroundColor: isActive ? 'primary.main' : 'transparent',
                      borderColor: 'primary.main',
                      color: isActive ? 'common.white' : 'text.primary',
                    },
                  }}
                >
                  {page}
                </Button>
              );
            })}
            <IconButton
              size={'small'}
              disabled={currentPage === safeTotalPages}
              onClick={() => changePage(currentPage + 1)}
              sx={{
                border: '1px solid',
                borderColor: 'primary.tableBorder',
                borderRadius: 1,
              }}
            >
              <ChevronRightIcon fontSize={'small'} />
            </IconButton>
          </Stack>
        </Box>
      )}

      <Dialog
        disableScrollLock
        open={testIdToDelete !== null}
        onClose={closeDeleteDialog}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogContent sx={{ pt: 3 }}>
          <Stack direction={'row'} alignItems={'center'} spacing={1.75} sx={{ mb: 1.75 }}>
            <Box
              sx={{
                alignItems: 'center',
                bgcolor: 'rgba(211,47,47,0.12)',
                borderRadius: '50%',
                color: 'error.main',
                display: 'inline-flex',
                flex: 'none',
                height: 44,
                justifyContent: 'center',
                width: 44,
              }}
            >
              <DeleteOutlineIcon />
            </Box>
            <Typography sx={{ fontSize: 19, fontWeight: 800 }}>
              {'Видалити результат тесту?'}
            </Typography>
          </Stack>
          <DialogContentText>
            {
              'Цей запис історії буде видалено. Слова у Vocabulary не зміняться.'
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant={'text'} onClick={closeDeleteDialog}>
            {'Скасувати'}
          </Button>
          <Button
            color={'error'}
            variant={'contained'}
            onClick={confirmDelete}
            startIcon={<DeleteOutlineIcon />}
          >
            {'Видалити'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TestHistory;
